import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  AlertSeverityEnum,
  // CajaPrimaria,
  // CajaPrimariaPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { CajaPrimaria, CajaPrimariaPagingRes } from '@/shared/interfaces/erp/configuracion-red/caja-primaria';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchCajasPrimarias = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetCajaPrimariasParams> = {}) => {
  return useQuery({
    queryKey: ['cajas-primarias-red', ...Object.values(params ?? {})],
    queryFn: () => getCajaPrimarias(params),
    enabled: enabled,
  });
};

export const useGetCajaPrimaria = (id: string) => {
  return useQuery({
    queryKey: ['cajas-primarias-red', id],
    queryFn: () => getCajaPrimaria(id),
  });
};

export const useCreateCajaPrimaria = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCajaPrimaria,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cajas-primarias-red'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Manga creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateCajaPrimaria = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCajaPrimaria,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cajas-primarias-red'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Manga actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteCajaPrimaria = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCajaPrimaria,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cajas-primarias-red'] });
    },
    onError: () => { },
  });
};

///* axios
export type GetCajaPrimariasParams = Partial<CajaPrimaria> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateCajaPrimariaParams = Omit<CajaPrimaria, 'id_caja_primaria'>;
export type UpdateCajaPrimariaParams = {
  id: string;
  data: Partial<CreateCajaPrimariaParams>;
};

export const getCajaPrimarias = async (params?: GetCajaPrimariasParams) => {
  const stateParams = { ...params };

  // filterByState
  // if (stateParams.filterByState === false && stateParams.estado === undefined) {
  //   delete stateParams.estado;
  // } else if (stateParams.filterByState !== false) {
  //   stateParams.estado = true;
  // }
  // delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<CajaPrimariaPagingRes>(`${urlBackend}api/v1/cajaprimaria/?${queryParams}`, true);
};

export const getCajaPrimaria = async (id: string) => {
  try {
    return await get<CajaPrimaria>(`${urlBackend}api/v1/cajaprimaria/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createCajaPrimaria = async (data: CreateCajaPrimariaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<CajaPrimaria>(`${urlBackend}api/v1/cajaprimaria/`, data, true);
};

export const updateCajaPrimaria = async ({ id, data }: UpdateCajaPrimariaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<CajaPrimaria>(`${urlBackend}api/v1/cajaprimaria/${id}/`, data, true);
};

export const deleteCajaPrimaria = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<CajaPrimaria>(`${urlBackend}api/v1/cajaprimaria/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};

//CUSTOMS
// =============================================================================
// cargas con excel
// =============================================================================
/***
 * Importar Excel Manga
 * 
*/
// export const useFetchCajaPrimariasImport = () => {
//   const queryClient = useQueryClient();

//   const setAlert = useUiStore.getState().setAlert;
//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


//   return useMutation({

//     mutationFn: createImportCajaPrimariasFromExcel,
//     onSuccess: data => {
//       queryClient.invalidateQueries({ queryKey: ['olts-create-array'] });
//       setAlert(true, data.message, data.status);
//     },

//     onError: (error => {
//       if (isAxiosError(error)) {
//         const respAxiosData = error.response?.data || {};
//         if (respAxiosData.code == 400)
//           return setAlert(true, `Se ha detectado un código duplicado. Por favor, corrige el archivo y vuelve a subirlo.`, AlertSeverityEnum.error)
//       }
//       handleAxiosError(error);
//     }),
//     onSettled: () => {
//       setIsGlobalLoading(false);

//     }
//   });
// };
// export const createImportCajaPrimariasFromExcel = async (data: { olts: any[] }) => {
//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
//   setIsGlobalLoading(true)
//   const res = await post<CajaPrimariaBase>(`${urlBackend}api/v1/importar-olts/`, data, true);
//   return res;
// };


/***
 * Importar Excel Manga Troncales
 * 
*/
// export const useFetchCajaPrimariasTroncalesImport = () => {
//   const queryClient = useQueryClient();

//   const setAlert = useUiStore.getState().setAlert;
//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


//   return useMutation({

//     mutationFn: createImportCajaPrimariasTroncalesFromExcel,
//     onSuccess: data => {
//       queryClient.invalidateQueries({ queryKey: ['oltsTroncales-create-array'] });
//       setAlert(true, data.message, data.status);
//     },

//     onError: (error => {
//       if (isAxiosError(error)) {
//         const respAxiosData = error.response?.data || {};
//         if (respAxiosData.code == 400)
//           return setAlert(true, `Se ha detectado un código duplicado. Por favor, corrige el archivo y vuelve a subirlo.`, AlertSeverityEnum.error)
//       }
//       handleAxiosError(error);
//     }),
//     onSettled: () => {
//       setIsGlobalLoading(false);

//     }
//   });
// };
// export const createImportCajaPrimariasTroncalesFromExcel = async (data: { oltsTroncales: any[] }) => {
//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
//   setIsGlobalLoading(true)
//   const { oltsTroncales } = data
//   const res = await post<CajaPrimariasTroncalBase>(`${urlBackend}api/v1/asociar-nap-con-olt/`, { olts: oltsTroncales }, true);
//   return res;
// };




// export const importCajaPrimariaS = async (data: CajaPrimariaBase) => {
//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
//   setIsGlobalLoading(true);

//   return post<CajaPrimaria>(`${urlBackend}api/v1/importar-olts`, data, true);
// };
